import {
  FIELD,
  KICK,
  CROSS_FINISH,
  AI,
  modeKick,
  modeSpeed,
  PLAYER,
  POSSESSION,
  NUTMEG,
  SKILL,
  createPassSnapshot,
  type Ball,
  type Player,
  type Possession,
  type ShotStyle,
  type TeamSide,
  type Vec2,
} from "@golazo/shared";
import { clamp, length, lerp, len2 } from "../core/math.js";
// Determinista entre motores: ver `core/detMath.ts`.
import { dacos, dcos, dpow, dsin } from "../core/detMath.js";
import type { Random } from "../core/random.js";
import { signed } from "../core/random.js";
import { tryApplyCrossFinishShot } from "./crossFinish.js";
import { passAngularError } from "../ai/passPhysics.js";

export type KickKind = "pass" | "shoot" | "cross";

export interface KickContext {
  kind: KickKind;
  /** Direccion pedida, normalizada o casi. */
  dir: Vec2;
  /** Carga de 0 a 1. */
  power: number;
  /** Golpeo dirigido (carga con K): precision maxima. */
  aiming: boolean;
  /** Control cerrado activo este tick. */
  /** Solo IA: semilla para dispersion reproducible. */
  random?: Random;
  /** Direccion ya ajustada (p. ej. IA): no volver a dispersar. */
  directionLocked?: boolean;
  /** Remate imparable al palo (remate de centro). */
  unstoppable?: boolean;
  /** Rivales: solo para resolver remate de centro imparable. */
  opponents?: readonly Player[];
  /** Estilo de remate (IA o inferido). */
  shotStyle?: ShotStyle;
  assistSpreadScale?: number;
  /**
   * Golpeo al vuelo (primera): no reposicionar el balon al pie. Si no, con
   * la ventana abierta el balon salta decenas de unidades hasta el jugador.
   */
  preserveBallPosition?: boolean;
}

export interface ResolvedKick {
  dx: number;
  dy: number;
  speed: number;
  /** Comba lateral (guiñada). */
  spin: number;
  /** Efecto vertical: + liftado (baja), − cortado (flota y muerde al botar). */
  topSpin: number;
}

/** Curva de carga: rapido al inicio, ultimo tramo exige mantener. */
export function chargeCurve(power: number): number {
  const p = clamp(power, 0, 1);
  return 1 - dpow(1 - p, 2.4);
}

/** Curva mas generosa solo para la UI de mira del humano. */
export function humanChargeCurve(power: number): number {
  const p = clamp(power, 0, 1);
  return 1 - dpow(1 - p, 1.75);
}

/** Potencia efectiva segun tipo de golpeo y carga. */
/**
 * Lo que `resolveKickPower` mira del contexto. Se nombra para que la mira
 * pueda pedir la misma cuenta sin fabricarse un `KickContext` entero a base de
 * casts — que es como el preview acaba divergiendo del golpeo de verdad.
 */
export type KickPowerContext = Pick<
  KickContext,
  "kind" | "power" | "aiming" | "shotStyle" | "unstoppable"
>;

export function resolveKickPower(ctx: KickPowerContext): number {
  const curved = chargeCurve(ctx.power);
  if (ctx.kind === "pass") {
    let scale = lerp(KICK.PASS_MIN_POWER, 1, Math.sqrt(curved));
    // Centro / cambio de juego: el ultimo tramo de carga empuja lejos.
    if (curved >= KICK.PASS_LONG_CHARGE) {
      const t = (curved - KICK.PASS_LONG_CHARGE) / (1 - KICK.PASS_LONG_CHARGE);
      scale *= lerp(1, KICK.PASS_LONG_BONUS, t);
    }
    return scale;
  }
  if (ctx.kind === "cross") {
    return lerp(KICK.CROSS_MIN_POWER, 1, Math.sqrt(curved));
  }

  let scale = lerp(KICK.MIN_POWER, 1, curved);

  if (ctx.aiming && ctx.kind === "shoot" && curved >= KICK.PERFECT_CHARGE) {
    scale *= KICK.PERFECT_CHARGE_BONUS;
  }

  // Colocada: apuntando con poca carga = menos potencia, mas control.
  const placed =
    ctx.shotStyle === "placed" ||
    (ctx.aiming && ctx.power <= KICK.FINESSE_CHARGE && ctx.shotStyle !== "chip");
  if (placed) {
    const t =
      ctx.shotStyle === "placed"
        ? clamp(ctx.power / KICK.FINESSE_CHARGE, 0, 1)
        : ctx.power / KICK.FINESSE_CHARGE;
    scale *= lerp(KICK.FINESSE_SPEED_MIN, KICK.FINESSE_SPEED_MAX, t);
  }

  if (ctx.shotStyle === "chip") {
    scale *= KICK.CHIP_SPEED_SCALE;
  } else if (ctx.shotStyle === "volley") {
    scale *= KICK.VOLLEY_SPEED_SCALE;
  }

  if (ctx.unstoppable) {
    scale *= CROSS_FINISH.SPEED_SCALE;
  }

  return scale;
}

/**
 * Dispersion angular maxima (rad). Apuntar anula error; correr sin mirar lo
 * multiplica. Fatiga y presion ensucian el remate.
 */
export function maxKickSpread(
  ctx: KickContext,
  runSpeed: number,
  player?: Player,
  passDist = 0,
  pressured = false,
): number {
  if (ctx.aiming) return 0;
  if (ctx.shotStyle === "chip") return KICK.SPREAD_STAND * 0.55;

  const speedRatio = clamp(runSpeed / PLAYER.MAX_SPEED, 0, 1);
  let spread = lerp(KICK.SPREAD_STAND, KICK.SPREAD_SPRINT, speedRatio);

  if (ctx.kind === "pass") spread *= KICK.PASS_SPREAD_SCALE;
  if (ctx.kind === "cross") spread *= KICK.PASS_SPREAD_SCALE * 0.85;

  if (player && (ctx.kind === "pass" || ctx.kind === "cross")) {
    const skillErr = passAngularError(player, passDist, pressured, ctx.kind);
    spread = Math.max(spread, skillErr);
  }

  if (player && ctx.kind === "shoot") {
    const shotDist = passDist > 0 ? passDist : 200;
    const skillErr = passAngularError(player, shotDist, pressured, "shoot");
    spread = Math.max(spread, skillErr);
    // Fatiga: piernas blandas → pie impreciso.
    spread += (1 - clamp(player.stamina, 0, 1)) * KICK.FATIGUE_SPREAD_GAIN;
    if (pressured) spread += KICK.PRESSURE_SPREAD_GAIN;
    if (ctx.shotStyle === "placed") spread *= 0.55;
    if (ctx.shotStyle === "power") spread *= 1.12;
  }

  return spread;
}

/** Desalineacion entre direccion pedida y hacia donde corre/mira el jugador. */
function misalignment(dir: Vec2, player: Player): number {
  const ref = length(player.vel) > 80 ? player.vel : player.facing;
  const len = len2(ref.x, ref.y);
  if (len < 1e-6) return 0;
  const dot = clamp((dir.x * ref.x + dir.y * ref.y) / len, -1, 1);
  return dacos(dot);
}

/**
 * Castigo por golpear cruzado a la carrera. Ver KICK.MISALIGN_GAIN para el
 * porque de las tres reglas (escala con velocidad, satura, y apuntar lo anula).
 */
function misalignPenalty(dir: Vec2, player: Player, _ctx: KickContext): number {
  const speedRatio = clamp(length(player.vel) / modeSpeed(PLAYER.MAX_SPEED), 0, 1);
  if (speedRatio < 1e-3) return 0;

  // sin(a/2): 0 al frente, 1 a la espalda. Satura en vez de crecer sin freno.
  const shape = dsin(misalignment(dir, player) / 2);
  // Sin tecla de apuntar el castigo es siempre el pleno: `ctx.aiming` NO sirve
  // para descontarlo porque la ruta humana lo pone a `true` siempre (al cargar
  // ya diriges con WASD), y eso dejaria el castigo plano para todo el mundo.
  const penalty = KICK.MISALIGN_GAIN * shape * speedRatio;
  // Carrera: quien pasa mejor golpea cruzado a la carrera con menos castigo.
  return player.talent ? penalty / player.talent.passing : penalty;
}

/** Carrera: cuanto de mas (o de menos) sale el balon segun el talento. */
function talentKickScale(player: Player, kind: KickKind): number {
  const talent = player.talent;
  if (!talent) return 1;
  if (kind === "shoot") return talent.shooting;
  if (kind === "pass" || kind === "cross") return 1 + (talent.passing - 1) * 0.5;
  return 1;
}

function rotateDir(dir: Vec2, angle: number): void {
  const c = dcos(angle);
  const s = dsin(angle);
  const x = dir.x;
  dir.x = x * c - dir.y * s;
  dir.y = x * s + dir.y * c;
}

/** Aplica dispersion determinista (humano) o aleatoria (IA). */
export function applyKickSpread(dir: Vec2, player: Player, ctx: KickContext): void {
  if (ctx.directionLocked) return;

  const passDist =
    ctx.kind === "pass" || ctx.kind === "cross"
      ? 160 + chargeCurve(ctx.power) * 540
      : ctx.kind === "shoot"
        ? 180 + chargeCurve(ctx.power) * 420
        : 0;
  const pressured =
    ctx.opponents?.some(
      (o) => len2(o.pos.x - player.pos.x, o.pos.y - player.pos.y) < AI.PRESSURE_RADIUS,
    ) ?? false;

  let spread = maxKickSpread(ctx, length(player.vel), player, passDist, pressured);
  spread += misalignPenalty(dir, player, ctx);
  // La asistencia reduce el error DESPUES de sumarlo todo, para que valga
  // tambien sobre el castigo cruzado y no solo sobre la dispersion base.
  spread *= ctx.assistSpreadScale ?? 1;
  if (spread <= 1e-6) return;

  if (ctx.random) {
    rotateDir(dir, signed(ctx.random) * spread);
    return;
  }

  const ref = length(player.vel) > 80 ? player.vel : player.facing;
  const cross = dir.x * ref.y - dir.y * ref.x;
  const sign = cross >= 0 ? 1 : -1;
  rotateDir(dir, sign * spread * 0.85);
}

/** Efecto / comba del golpeo. */
export function resolveKickSpin(
  dx: number,
  dy: number,
  player: Player,
  ctx: KickContext,
): number {
  if (ctx.unstoppable) return 0;

  const runSpeed = length(player.vel);

  if (ctx.kind === "cross") {
    // Comba hacia el centro del area: el centro "cae" al rematador.
    const inward = Math.sign(FIELD.HEIGHT / 2 - player.pos.y) || 1;
    const sideSpin = inward * (0.55 + ctx.power * 0.45) * KICK.CROSS_SPIN_GAIN;
    const runSpin =
      runSpeed > 1e-3
        ? (dx * (player.vel.y / runSpeed) - dy * (player.vel.x / runSpeed)) *
          (runSpeed / PLAYER.MAX_SPEED) *
          KICK.CROSS_SPIN_GAIN *
          0.35
        : 0;
    return sideSpin + runSpin;
  }

  if (ctx.kind === "shoot" && runSpeed <= 1e-3) {
    // Tiro parado con mira: curva de colocacion (trivela / rosca).
    if (ctx.aiming) {
      const cross = dx * player.facing.y - dy * player.facing.x;
      return cross * KICK.FINESSE_STAND_SPIN * (0.55 + ctx.power * 0.45);
    }
    return 0;
  }

  if (runSpeed <= 1e-3) return 0;

  const cross = dx * (player.vel.y / runSpeed) - dy * (player.vel.x / runSpeed);
  const gain = ctx.kind === "pass" ? KICK.PASS_SPIN_GAIN : KICK.SPIN_GAIN;
  let spin = cross * (runSpeed / PLAYER.MAX_SPEED) * gain;

  if (ctx.aiming && ctx.kind === "shoot" && ctx.power <= KICK.FINESSE_CHARGE) {
    spin *= KICK.FINESSE_SPIN_SCALE;
  }

  if (ctx.shotStyle === "placed") {
    spin *= KICK.FINESSE_SPIN_SCALE;
  }

  if ((ctx.kind === "shoot" && ctx.power >= 0.72) || ctx.shotStyle === "power") {
    spin *= KICK.POWER_SHOT_SPIN_SCALE;
  }

  if (ctx.shotStyle === "chip") {
    spin *= 0.2;
  }


  if (ctx.aiming && ctx.kind === "pass") {
    spin *= KICK.AIMED_PASS_SPIN_SCALE;
  }

  if (ctx.aiming && ctx.kind === "shoot") {
    spin *= KICK.AIMED_SPIN_SCALE;
  }

  return spin + (ctx.aiming ? sweepCurl(player.aimSweep, ctx.kind, ctx.power) : 0);
}

/**
 * Comba por gesto de mira: el ajuste de ultima hora se convierte en efecto.
 *
 * Va SUMADA y no multiplicada por `AIMED_SPIN_SCALE`. Ese factor existe para
 * que un golpeo apuntado salga limpio en vez de rosqueado por la inercia de la
 * carrera; aplicarselo tambien a esto mataria la mecanica entera, porque
 * apuntar es precisamente cuando se comba a proposito.
 *
 * El signo se hereda tal cual del giro de la mira, y no por casualidad:
 * `ball.spin` rota la velocidad con la misma convencion con la que
 * `rotateAim` gira la mira. Girar la mira hacia un lado y que el balon se vaya
 * hacia el otro seria imposible de aprender.
 */
export function sweepCurl(
  aimSweep: number,
  kind: KickKind,
  power: number,
): number {
  // El centro ya comba solo hacia el area; sumarle el gesto lo haria ingobernable.
  if (kind === "cross") return 0;

  const mag = Math.abs(aimSweep);
  if (mag <= KICK.SWEEP_DEADZONE) return 0;

  const span = KICK.SWEEP_FULL - KICK.SWEEP_DEADZONE;
  const eff = clamp((mag - KICK.SWEEP_DEADZONE) / span, 0, 1);
  let curl = Math.sign(aimSweep) * eff * KICK.SWEEP_SPIN;

  if (kind === "pass") curl *= KICK.SWEEP_PASS_SCALE;
  // Colocado envuelve, potente golpea de pleno. Entre los dos hay un abanico
  // continuo, que es donde vive la decision: rosca o potencia.
  else curl *= lerp(KICK.SWEEP_FINESSE_SCALE, KICK.SWEEP_POWER_SCALE, chargeCurve(power));

  return curl;
}

/**
 * Cuanto se dobla el disparo por unidad de mundo recorrida (rad/u).
 *
 * Existe para que la MIRA pueda dibujar la curva de verdad. La comba depende
 * de la velocidad —el mismo efecto dobla menos un misil que un balon lento—,
 * asi que la mira no puede inventarse la forma: tiene que salir de los mismos
 * numeros que el golpeo. Una comba que se pinta distinta de como sale es peor
 * que no pintarla.
 */
/**
 * Velocidad de salida de un golpeo apuntado. Sale de las mismas funciones que
 * el golpeo real: cualquier calibracion que se invente su propia formula acaba
 * midiendo un juego que no existe.
 */
export function kickSpeedFor(kind: KickKind, power: number): number {
  return kickBaseSpeed(kind) * resolveKickPower({ kind, power, aiming: true });
}

export function aimPreviewCurvature(
  player: Player,
  kind: KickKind,
  power: number,
): number {
  const curl = sweepCurl(player.aimSweep, kind, power);
  if (curl === 0) return 0;
  const speed = kickSpeedFor(kind, power);
  return speed > 1e-3 ? curl / speed : 0;
}

/**
 * Efecto vertical del golpeo. Se ata a gestos que YA existen, no a un boton
 * nuevo: el jugador no tiene que aprender nada, solo notar que cada golpeo se
 * comporta como debe.
 *
 *  - **Vaselina** (`chip`): muy cortado. Sube, flota y al caer muerde y se
 *    queda. Es lo que la convierte en un recurso y no en un globo.
 *  - **Disparo potente cargado**: liftado. Baja antes de tiempo — el trallazo
 *    que se cuela bajo el larguero.
 *  - **Volea**: liftado moderado; el balon sale hacia abajo, no en parabola.
 *  - **Centro**: algo cortado, para que aguante el vuelo hasta el area.
 *  - **Colocado** (`placed`): sin efecto vertical. Su gracia es la comba
 *    lateral, y sumar los dos ejes lo volveria imposible de leer.
 */
function resolveKickTopSpin(ctx: KickContext): number {
  if (ctx.shotStyle === "chip") return -KICK.TOPSPIN_CHIP;
  if (ctx.shotStyle === "placed") return 0;
  if (ctx.shotStyle === "volley") return KICK.TOPSPIN_VOLLEY;
  if (ctx.kind === "cross") return -KICK.TOPSPIN_CROSS;

  if (ctx.kind === "shoot") {
    // Solo el disparo realmente cargado lifta: un toque seco no dobla nada.
    const charge = clamp((ctx.power - KICK.TOPSPIN_MIN_CHARGE) / (1 - KICK.TOPSPIN_MIN_CHARGE), 0, 1);
    return KICK.TOPSPIN_SHOT * charge;
  }

  return 0;
}

function resolveKickLoft(ctx: KickContext): number {
  if (ctx.shotStyle === "chip") return KICK.CHIP_LOFT;
  if (ctx.shotStyle === "volley") return KICK.VOLLEY_LOFT;

  const curved = chargeCurve(ctx.power);
  if (ctx.kind === "cross") {
    return KICK.LOFT_CROSS * (0.65 + curved * 0.35);
  }
  if (ctx.kind === "pass") {
    if (curved < 0.26) return KICK.LOFT_PASS_MIN;
    if (curved < 0.52) {
      return lerp(KICK.LOFT_PASS_MIN, KICK.LOFT_PASS_MID, (curved - 0.26) / 0.26);
    }
    if (curved < 0.76) {
      return lerp(KICK.LOFT_PASS_MID, KICK.LOFT_PASS_LONG, (curved - 0.52) / 0.24);
    }
    return lerp(KICK.LOFT_PASS_LONG, KICK.LOFT_PASS_SWITCH, (curved - 0.76) / 0.24);
  }
  if (ctx.kind !== "shoot") return 0;

  let loft = lerp(KICK.LOFT_SHOOT_MIN, KICK.LOFT_SHOOT_MAX, curved);
  if (
    ctx.shotStyle === "placed" ||
    (ctx.aiming && ctx.power <= KICK.FINESSE_CHARGE)
  ) {
    loft *= KICK.FINESSE_LOFT_SCALE;
  }
  return loft;
}

function kickBaseSpeed(kind: KickKind): number {
  if (kind === "shoot") return modeKick(KICK.SHOOT_SPEED);
  if (kind === "cross") return modeKick(KICK.CROSS_SPEED);
  return modeKick(KICK.PASS_SPEED);
}

function kickInherit(kind: KickKind, ctx: KickContext): number {
  if (kind === "pass") {
    return ctx.aiming ? KICK.PASS_AIMED_INHERIT : KICK.PASS_INHERIT;
  }
  if (kind === "cross") return KICK.CROSS_INHERIT;
  if (ctx.aiming && kind === "shoot") return KICK.AIMED_INHERIT;
  return KICK.INHERIT;
}

/**
 * Cuanto efecto suma el aftertouch en este tick (`KICK.AFTERTOUCH_*`).
 *
 * `dirX`/`dirY`: hacia donde vuela el balon (unitario). `move`: lo que pide
 * quien chuto. Solo cuenta lo que se aparta DE LADO del vuelo: seguir
 * corriendo detras del balon no lo dobla. El signo es el de `ball.spin` en
 * `stepBall`: positivo gira el vuelo hacia donde apunta el producto cruzado,
 * asi que pulsar hacia un lado dobla el balon hacia ESE lado.
 */
export function aftertouchSpin(dirX: number, dirY: number, move: Vec2, dt: number, added: number): number {
  const mLen = len2(move.x, move.y);
  if (mLen < 1e-6) return 0;
  const side = (dirX * move.y - dirY * move.x) / mLen;
  if (Math.abs(side) < KICK.AFTERTOUCH_DEADZONE) return 0;
  const next = clamp(added + side * KICK.AFTERTOUCH_RATE * dt, -KICK.AFTERTOUCH_MAX, KICK.AFTERTOUCH_MAX);
  return next - added;
}

/** Como sale el balon de un golpeo: donde, a que velocidad y con que efectos. */
export interface KickLaunch {
  x: number;
  y: number;
  vx: number;
  vy: number;
  spin: number;
  topSpin: number;
  loftVel: number;
}

/**
 * La salida del balon, en una sola formula para dos usuarios: `kickBall`, que
 * golpea de verdad, y la mira (`game/kickPreview.ts`), que simula el vuelo
 * para enseñar a donde va. Con dos copias de la cuenta, la mira acabaria
 * prometiendo un disparo distinto del que sale — que es justo lo que pasaba
 * con el golpeo cruzado a la carrera, que la flecha no enseñaba.
 */
export function kickLaunch(player: Player, ctx: KickContext, ballRadius: number): KickLaunch {
  const { dx, dy, speed, spin, topSpin } = resolveKick(player, ctx);
  const inherit = kickInherit(ctx.kind, ctx);
  return {
    x: player.pos.x + dx * (player.radius + ballRadius + 2),
    y: player.pos.y + dy * (player.radius + ballRadius + 2),
    vx: dx * speed + player.vel.x * inherit,
    vy: dy * speed + player.vel.y * inherit,
    spin,
    topSpin,
    loftVel: resolveKickLoft(ctx),
  };
}

export function resolveKick(player: Player, ctx: KickContext): ResolvedKick {
  const dir = { x: ctx.dir.x, y: ctx.dir.y };
  const len = len2(dir.x, dir.y);
  if (len > 1e-6) {
    dir.x /= len;
    dir.y /= len;
  } else {
    dir.x = player.facing.x;
    dir.y = player.facing.y;
  }

  applyKickSpread(dir, player, ctx);

  const base = kickBaseSpeed(ctx.kind) * resolveKickPower(ctx);
  const speed = player.talent ? base * talentKickScale(player, ctx.kind) : base;
  const spin = resolveKickSpin(dir.x, dir.y, player, ctx);
  const topSpin = resolveKickTopSpin(ctx);

  return {
    dx: dir.x,
    dy: dir.y,
    speed,
    spin,
    topSpin,
  };
}

/**
 * El saque de centro se juega HACIA ATRAS.
 *
 * En el reglamento de verdad el balon puede salir en cualquier direccion desde
 * 2016 (antes tenia que ir hacia DELANTE), asi que esto no es una regla de
 * futbol: es una regla de este juego, y esta puesta a proposito. Sin ella el
 * saque de centro era una salida a la contra gratis — se golpeaba en largo por
 * encima de un equipo que acababa de colocarse, y marcar te dejaba peor puesto
 * que encajar.
 *
 * Se PROYECTA en vez de rechazar el golpeo. Bloquear la pulsacion se siente
 * como que el juego no responde; proyectar deja que el balon salga siempre, y
 * si apuntas hacia delante sale en horizontal, que es la lectura correcta de
 * "por ahi todavia no".
 */
function backwardKickDir(player: Player, dir: Vec2): Vec2 {
  const forward = player.side === "home" ? 1 : -1;
  const along = dir.x * forward;
  if (along <= 0) return dir;

  // Fuera la componente hacia delante y se renormaliza lo que queda.
  const y = dir.y;
  const len = Math.abs(y);
  if (len < 1e-3) {
    // Apuntando en linea recta al gol rival no queda nada lateral que salvar:
    // sale hacia la propia porteria, que es el pase atras de libro.
    return { x: -forward, y: 0 };
  }
  return { x: 0, y: y / len };
}

export function kickBall(
  ball: Ball,
  player: Player,
  possession: Possession,
  ctx: KickContext,
  allPlayers?: readonly Player[],
): void {
  if (possession.kickoff && possession.kickoff.takerId === player.id) {
    ctx = { ...ctx, dir: backwardKickDir(player, ctx.dir) };
  }

  if (ctx.opponents && ctx.kind === "shoot") {
    tryApplyCrossFinishShot(player, possession, ctx.opponents, ctx);
  }

  possession.pendingKickKind = ctx.kind;
  possession.pendingKickSide = player.side;

  if ((ctx.kind === "pass" || ctx.kind === "cross") && allPlayers) {
    possession.passSnapshot = createPassSnapshot(
      player,
      ball.pos.x,
      ball.pos.y,
      ctx.kind,
      allPlayers,
    );
  } else {
    possession.passSnapshot = null;
  }

  const launch = kickLaunch(player, ctx, ball.radius);

  ball.spin = launch.spin;
  ball.topSpin = launch.topSpin;
  ball.loft = 0;
  ball.prevLoft = 0;
  ball.loftVel = launch.loftVel;
  ball.unstoppable = ctx.unstoppable === true;

  if (!ctx.preserveBallPosition) {
    ball.pos.x = launch.x;
    ball.pos.y = launch.y;
  }
  ball.vel.x = launch.vx;
  ball.vel.y = launch.vy;

  possession.ownerId = null;
  possession.duelWith = null;
  possession.duelTime = 0;
  possession.lockout.set(player.id, POSSESSION.RECAPTURE_LOCKOUT);
  // Golpeado el balon, el saque esta jugado: se acaban las dos restricciones.
  possession.kickoff = null;
}

/** ¿Estas en carril de centro (banda + ultimo tramo)? */
export function isInCrossingZone(player: Player): boolean {
  const wing =
    player.pos.y <= KICK.CROSS_WING_BAND || player.pos.y >= FIELD.HEIGHT - KICK.CROSS_WING_BAND;
  if (!wing) return false;

  const attackX =
    player.side === "home"
      ? FIELD.WIDTH * KICK.CROSS_ATTACK_DEPTH
      : FIELD.WIDTH * (1 - KICK.CROSS_ATTACK_DEPTH);
  return player.side === "home" ? player.pos.x >= attackX : player.pos.x <= attackX;
}

/**
 * Un pase desde banda cuenta como centro si apunta hacia el area (hacia
 * dentro y hacia delante), no si va paralelo a la linea de fondo.
 */
export function shouldCross(player: Player, dir: Vec2): boolean {
  if (!isInCrossingZone(player)) return false;

  const len = len2(dir.x, dir.y);
  if (len < 1e-6) return false;
  const dx = dir.x / len;
  const dy = dir.y / len;

  const forward = player.side === "home" ? dx : -dx;
  const inward = Math.sign(FIELD.HEIGHT / 2 - player.pos.y) * dy;

  // Hacia el area: componente interior clara y algo de profundidad.
  return inward > 0.28 && forward > -0.15;
}

/** Punto tipico de caida de un centro (zona de penalti / segundo palo). */
export function defaultCrossTarget(side: TeamSide, fromY: number): Vec2 {
  const goalX = side === "home" ? FIELD.WIDTH : 0;
  const dir = side === "home" ? -1 : 1;
  const boxX = goalX + dir * (FIELD.BOX_WIDTH * 0.55);
  // Segundo palo: cae al lado opuesto de desde donde viene el centro.
  const farPost = fromY < FIELD.HEIGHT / 2 ? FIELD.HEIGHT / 2 + 70 : FIELD.HEIGHT / 2 - 70;
  return {
    x: clamp(boxX, FIELD.BOX_WIDTH * 0.35, FIELD.WIDTH - FIELD.BOX_WIDTH * 0.35),
    y: clamp(
      farPost,
      FIELD.HEIGHT / 2 - KICK.CROSS_BOX_HALF_WIDTH,
      FIELD.HEIGHT / 2 + KICK.CROSS_BOX_HALF_WIDTH,
    ),
  };
}

/**
 * Stick apuntando hacia atras respecto al facing (sin rival obligatorio).
 */
function readRearStick(player: Player, move: Vec2 | null): Vec2 | null {
  const mLen = move ? len2(move.x, move.y) : 0;
  if (mLen < 0.35 || !move) return null;

  const mx = move.x / mLen;
  const my = move.y / mLen;

  const fLen = len2(player.facing.x, player.facing.y) || 1;
  const fx = player.facing.x / fLen;
  const fy = player.facing.y / fLen;

  if (fx * mx + fy * my > -NUTMEG.REAR_MOVE_COS) return null;
  return { x: mx, y: my };
}

/** Rival opcional en el arco trasero (solo para duda / juice). */
export function findRearSkillTarget(
  player: Player,
  opponents: readonly Player[],
  move: Vec2 | null,
  minSpeed: number = NUTMEG.REAR_MIN_SPEED,
): Player | null {
  const stick = readRearStick(player, move);
  if (!stick) return null;

  if (minSpeed > 0) {
    const speed = len2(player.vel.x, player.vel.y);
    if (speed < minSpeed) return null;
  }

  const fLen = len2(player.facing.x, player.facing.y) || 1;
  const fx = player.facing.x / fLen;
  const fy = player.facing.y / fLen;

  let best: Player | null = null;
  let bestDist = Infinity;
  for (const rival of opponents) {
    if (rival.side === player.side || rival.role === "gk") continue;
    const dx = rival.pos.x - player.pos.x;
    const dy = rival.pos.y - player.pos.y;
    const dist = len2(dx, dy);
    if (dist < NUTMEG.MIN_DIST || dist > NUTMEG.MAX_DIST) continue;
    const dirX = dx / dist;
    const dirY = dy / dist;
    if (fx * dirX + fy * dirY > NUTMEG.REAR_CONE_COS) continue;
    if (stick.x * dirX + stick.y * dirY < NUTMEG.REAR_AIM_COS) continue;
    if (dist < bestDist) {
      best = rival;
      bestDist = dist;
    }
  }
  return best;
}

function hesitateRearRivals(
  player: Player,
  opponents: readonly Player[],
  dirX: number,
  dirY: number,
  scale: number,
): Player | null {
  let best: Player | null = null;
  let bestDist = Infinity;
  for (const rival of opponents) {
    if (rival.side === player.side || rival.role === "gk") continue;
    const dx = rival.pos.x - player.pos.x;
    const dy = rival.pos.y - player.pos.y;
    const dist = len2(dx, dy);
    if (dist > SKILL.FAKE_RADIUS) continue;
    const dirToRivalX = dx / (dist || 1);
    const dirToRivalY = dy / (dist || 1);
    if (dirX * dirToRivalX + dirY * dirToRivalY < 0.45) continue;
    rival.hesitateTime = Math.max(rival.hesitateTime, NUTMEG.HESITATE * scale);
    if (dist < bestDist) {
      best = rival;
      bestDist = dist;
    }
  }
  return best;
}

export interface BackNutmegResult {
  applied: boolean;
  victim: Player | null;
}

/**
 * Caño hacia atras: stick hacia atras + O en carrera. Marcador opcional.
 */
export function applyBackNutmeg(
  ball: Ball,
  player: Player,
  possession: Possession,
  opponents: readonly Player[],
  move: Vec2 | null,
): BackNutmegResult {
  if (possession.ownerId !== player.id) return { applied: false, victim: null };
  if (player.trapTime > 0 || player.dashCooldown > 0 || player.skillCooldown > 0) {
    return { applied: false, victim: null };
  }

  const stick = readRearStick(player, move);
  if (!stick) return { applied: false, victim: null };

  const speed = len2(player.vel.x, player.vel.y);
  if (speed < NUTMEG.REAR_MIN_SPEED) return { applied: false, victim: null };

  const dirX = stick.x;
  const dirY = stick.y;
  const push = NUTMEG.PUSH_BASE * NUTMEG.REAR_PUSH_SCALE;
  ball.vel.x = dirX * push;
  ball.vel.y = dirY * push;
  ball.loftVel = NUTMEG.LOFT;
  ball.spin = 0;
  ball.topSpin = 0;

  possession.ownerId = null;
  possession.duelWith = null;
  possession.duelTime = 0;

  const fLen = len2(player.facing.x, player.facing.y) || 1;
  const burst = modeSpeed(SKILL.BURST_SPEED) * NUTMEG.REAR_BURST_SCALE;
  player.vel.x = (player.facing.x / fLen) * burst;
  player.vel.y = (player.facing.y / fLen) * burst;
  player.burstTime = Math.max(player.burstTime, NUTMEG.BURST_TIME);
  player.dashCooldown = PLAYER.DASH_COOLDOWN;
  player.skillCooldown = Math.max(player.skillCooldown, NUTMEG.COOLDOWN);

  const victim = hesitateRearRivals(player, opponents, dirX, dirY, 1);
  return { applied: true, victim };
}

/**
 * Fake / engano: no sueltas el balon. Los rivales cercanos dudan y tu sales
 * con un cambio de ritmo corto. Es el gesto del toque seco de K.
 */
export function applyFake(player: Player, opponents: readonly Player[]): boolean {
  if (player.fakeCooldown > 0 || player.trapTime > 0) return false;

  player.fakeCooldown = SKILL.FAKE_COOLDOWN;
  player.burstTime = Math.max(player.burstTime, SKILL.FAKE_BURST);

  const burst = modeSpeed(SKILL.BURST_SPEED) * 0.72;
  const len = len2(player.facing.x, player.facing.y) || 1;
  player.vel.x = (player.facing.x / len) * burst;
  player.vel.y = (player.facing.y / len) * burst;

  for (const rival of opponents) {
    if (rival.side === player.side) continue;
    const d = len2(rival.pos.x - player.pos.x, rival.pos.y - player.pos.y);
    if (d <= SKILL.FAKE_RADIUS) {
      rival.hesitateTime = Math.max(rival.hesitateTime, SKILL.FAKE_HESITATE);
    }
  }
  return true;
}

/** True si el stick es un corte lateral respecto al facing. */
export function isLateralCut(facing: Vec2, move: Vec2): boolean {
  const mLen = len2(move.x, move.y);
  if (mLen < 0.35) return false;
  const fLen = len2(facing.x, facing.y) || 1;
  const dot = (facing.x / fLen) * (move.x / mLen) + (facing.y / fLen) * (move.y / mLen);
  return Math.abs(dot) <= SKILL.CUT_MAX_DOT;
}

/**
 * Corte seco: toque lateral del balon + burst en la direccion del stick.
 * Mantiene posesion; el marcador cercano duda.
 */
export function applyCut(
  ball: Ball,
  player: Player,
  possession: Possession,
  opponents: readonly Player[],
  move: Vec2,
): boolean {
  if (possession.ownerId !== player.id) return false;
  if (player.fakeCooldown > 0 || player.trapTime > 0) return false;
  if (!isLateralCut(player.facing, move)) return false;

  const mLen = len2(move.x, move.y) || 1;
  const mx = move.x / mLen;
  const my = move.y / mLen;

  player.fakeCooldown = SKILL.CUT_COOLDOWN;
  player.dashCooldown = Math.max(player.dashCooldown, PLAYER.DASH_COOLDOWN * 0.55);
  player.burstTime = Math.max(player.burstTime, SKILL.CUT_BURST);

  const burst = modeSpeed(SKILL.BURST_SPEED) * SKILL.CUT_BURST_SCALE;
  player.vel.x = mx * burst;
  player.vel.y = my * burst;
  player.facing.x = mx;
  player.facing.y = my;
  player.aimDir.x = mx;
  player.aimDir.y = my;

  ball.vel.x = mx * SKILL.CUT_BALL_PUSH + player.vel.x * 0.12;
  ball.vel.y = my * SKILL.CUT_BALL_PUSH + player.vel.y * 0.12;
  ball.loft = 0;
  ball.loftVel = 0;
  ball.spin = 0;
  ball.topSpin = 0;

  for (const rival of opponents) {
    if (rival.side === player.side) continue;
    const d = len2(rival.pos.x - player.pos.x, rival.pos.y - player.pos.y);
    if (d <= SKILL.CUT_RADIUS) {
      rival.hesitateTime = Math.max(rival.hesitateTime, SKILL.CUT_HESITATE);
    }
  }
  return true;
}

/** Rival en el cono frontal para caño, o null. */
export function findNutmegTarget(
  player: Player,
  opponents: readonly Player[],
  move: Vec2 | null,
): Player | null {
  const fLen = len2(player.facing.x, player.facing.y) || 1;
  let fx = player.facing.x / fLen;
  let fy = player.facing.y / fLen;
  const mLen = move ? len2(move.x, move.y) : 0;
  if (mLen > 0.35 && move) {
    // El stick manda si apunta hacia delante; si no, facing.
    const mx = move.x / mLen;
    const my = move.y / mLen;
    if (fx * mx + fy * my > 0.25) {
      fx = mx;
      fy = my;
    }
  }

  let best: Player | null = null;
  let bestDist = Infinity;
  for (const rival of opponents) {
    if (rival.side === player.side || rival.role === "gk") continue;
    const dx = rival.pos.x - player.pos.x;
    const dy = rival.pos.y - player.pos.y;
    const dist = len2(dx, dy);
    if (dist < NUTMEG.MIN_DIST || dist > NUTMEG.MAX_DIST) continue;
    const dirX = dx / dist;
    const dirY = dy / dist;
    if (fx * dirX + fy * dirY < NUTMEG.CONE_COS) continue;
    if (dist < bestDist) {
      best = rival;
      bestDist = dist;
    }
  }
  return best;
}

/**
 * Caño: suelta el balon a traves del rival con loft corto y sales con burst.
 * Devuelve el rival canado, o null si no aplica.
 */
export function applyNutmeg(
  ball: Ball,
  player: Player,
  possession: Possession,
  opponents: readonly Player[],
  move: Vec2 | null,
): Player | null {
  if (possession.ownerId !== player.id) return null;
  if (player.trapTime > 0 || player.dashCooldown > 0 || player.skillCooldown > 0) return null;

  const rival = findNutmegTarget(player, opponents, move);
  if (!rival) return null;

  const dx = rival.pos.x - player.pos.x;
  const dy = rival.pos.y - player.pos.y;
  const dist = len2(dx, dy) || 1;
  const dirX = dx / dist;
  const dirY = dy / dist;

  const push = NUTMEG.PUSH_BASE + dist * NUTMEG.PUSH_PER_DIST;
  ball.vel.x = dirX * push;
  ball.vel.y = dirY * push;
  ball.loftVel = NUTMEG.LOFT;
  ball.spin = 0;
  ball.topSpin = 0;

  possession.ownerId = null;
  possession.duelWith = null;
  possession.duelTime = 0;

  const burst = modeSpeed(SKILL.BURST_SPEED) * NUTMEG.BURST_SCALE;
  player.vel.x = dirX * burst;
  player.vel.y = dirY * burst;
  player.facing.x = dirX;
  player.facing.y = dirY;
  player.aimDir.x = dirX;
  player.aimDir.y = dirY;
  player.burstTime = Math.max(player.burstTime, NUTMEG.BURST_TIME);
  player.dashCooldown = PLAYER.DASH_COOLDOWN;
  player.skillCooldown = Math.max(player.skillCooldown, NUTMEG.COOLDOWN);

  rival.hesitateTime = Math.max(rival.hesitateTime, NUTMEG.HESITATE);
  return rival;
}
